import { useState, useRef, useEffect, useMemo } from "react";
import { Upload, Button, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import styled from "styled-components";

export type FileUploadMode = 'all' | 'slide' | 'image';
interface FileUploadInputProps {
  value?: any;
  onChange?: (value: any) => void;
  accept?: string;
  mode?: FileUploadMode; // 모드 설정 (image | slide)
  allowedExtensions?: string[]; // 허용할 확장자 목록 (우선순위 높음)
  allowedExtensionsDisplay?: string[]; // 화면에 표시할 확장자 목록 (표시 전용)
  maxSize?: number; // 개별 파일 최대 크기 (MB 단위)
  maxTotalSize?: number; // 전체 파일 최대 크기 (MB 단위)
  multiple?: boolean;
  enableScroll?: boolean; // 파일 목록 스크롤 활성화 여부
  maxHeight?: string; // 스크롤 영역 최대 높이 (enableScroll이 true일 때 적용)
  showSuccessMessage?: boolean; // 파일 추가 성공 메시지 표시 여부
}

// 슬라이드 전용 확장자 (검증 용)
export const SLIDE_EXTENSIONS = ['.svs', '.ndpi', '.tif', '.tiff', '.zip'];
// 슬라이드 전용 확장자 (뷰 용)
export const SLIDE_EXTENSIONS_DISPLAY = ['.svs', '.ndpi', '.tif', '.tiff', '.zip (mrxs)'];

// 이미지 전용 확장자 (검증 용)
export const IMAGE_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.bmp', '.webp'];
// 이미지 전용 확장자 (뷰 용)
export const IMAGE_EXTENSIONS_DISPLAY = ['.jpeg', '.jpg', '.png', '.bmp', '.webp'];

// 모드별 기본 설정값
const MODE_CONFIG = {
  all: {
    extensions: [...SLIDE_EXTENSIONS, ...IMAGE_EXTENSIONS],
    extensionsDisplay: [...IMAGE_EXTENSIONS_DISPLAY, ...SLIDE_EXTENSIONS_DISPLAY],
    defaultMaxSize: 6000,
    defaultMaxTotalSize: 30000
  },
  slide: {
    extensions: SLIDE_EXTENSIONS,
    extensionsDisplay: SLIDE_EXTENSIONS_DISPLAY,
    defaultMaxSize: 6000,      // 6GB
    defaultMaxTotalSize: 30000 // 30GB
  },
  image: {
    extensions: IMAGE_EXTENSIONS,
    extensionsDisplay: IMAGE_EXTENSIONS_DISPLAY,
    defaultMaxSize: 50,       // 50MB (이미지용 기본값)
    defaultMaxTotalSize: 500  // 500MB (이미지용 기본값)
  }
};

// 기본 허용 확장자 (검증용 - 슬라이드 + 이미지)
const DEFAULT_ALLOWED_EXTENSIONS = [...SLIDE_EXTENSIONS, ...IMAGE_EXTENSIONS];

// 기본 허용 확장자 (표시용 - 슬라이드 + 이미지)
const DEFAULT_ALLOWED_EXTENSIONS_DISPLAY = [ ...IMAGE_EXTENSIONS, ...SLIDE_EXTENSIONS_DISPLAY];

const FileUploadInput = ({
  value,
  onChange,
  mode = 'all', // 기본값: slide
  accept,
  allowedExtensions,
  allowedExtensionsDisplay,
  maxSize,        // props로 전달받지 않으면 내부 로직에서 default값 할당
  maxTotalSize,   // props로 전달받지 않으면 내부 로직에서 default값 할당
  multiple = true,
  enableScroll = true,
  maxHeight = "300px",
  showSuccessMessage = true
}: FileUploadInputProps) => {
  // console.log("FileUploadInput rendered with:", { value, onChange: !!onChange });
  const [fileList, setFileList] = useState<any[]>(value?.fileList || []); // 업로드할 파일 관리
  const [isDragging, setIsDragging] = useState(false); // 파일 드래그&드랍 가능 여부 설정
  const messageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const totalAddedRef = useRef<number>(0);

  const config = useMemo(() => {
    const safeMode = MODE_CONFIG[mode] ? mode : 'all';
    const currentModeConfig = MODE_CONFIG[safeMode];
    
    const finalExtensions = allowedExtensions || 
      (accept ? accept.split(',').map(ext => ext.trim()) : currentModeConfig.extensions);

    const finalExtensionsDisplay = allowedExtensionsDisplay || 
      (allowedExtensions ? allowedExtensions : currentModeConfig.extensionsDisplay);

    const finalAccept = accept || finalExtensions.join(',');

    const finalMaxSize = maxSize ?? currentModeConfig.defaultMaxSize;
    const finalMaxTotalSize = maxTotalSize ?? currentModeConfig.defaultMaxTotalSize;

    return {
      extensions: finalExtensions,
      extensionsDisplay: finalExtensionsDisplay,
      accept: finalAccept,
      maxSize: finalMaxSize,
      maxTotalSize: finalMaxTotalSize
    };
  }, [mode, allowedExtensions, allowedExtensionsDisplay, accept, maxSize, maxTotalSize]);

  // value prop이 변경되면 fileList 동기화 (undefined/null이면 초기화)
  useEffect(() => {
    if (value?.fileList) {
      setFileList(value.fileList);
    } else if (value === undefined || value === null) {
      // Form resetFields 시 value가 undefined가 됨 -> 초기화
      setFileList([]);
    }
  }, [value]);

  // 검증용 확장자: allowedExtensions가 제공되면 우선 사용, 없으면 accept를 파싱, 둘 다 없으면 기본값 사용
  const ALLOWED_EXTENSIONS = allowedExtensions ||
    (accept ? accept.split(',').map(ext => ext.trim()) : DEFAULT_ALLOWED_EXTENSIONS);

  // 표시용 확장자: allowedExtensionsDisplay가 제공되면 우선 사용, 없으면 검증용과 동일하게
  const ALLOWED_EXTENSIONS_DISPLAY = allowedExtensionsDisplay ||
    (allowedExtensions ? allowedExtensions : DEFAULT_ALLOWED_EXTENSIONS_DISPLAY);

  // accept 속성 자동 생성 (검증용 확장자 사용)
  const acceptAttribute = accept || ALLOWED_EXTENSIONS.join(',');

  /* 전체 파일 크기 계산 (MB 단위) */
  const getTotalFileSize = (files: any[]) => {
    return files.reduce((total, file) => {
      const fileObj = file.originFileObj || file;
      return total + (fileObj.size / 1024 / 1024);
    }, 0);
  };

  /* 파일 중복 체크 (파일명 + 파일 크기로 비교) */
  const isDuplicateFile = (file: File, currentList: any[] = fileList) => {
    return currentList.some(existingFile => {
      const existingOrigin = existingFile.originFileObj || existingFile;
      return existingOrigin.name === file.name && existingOrigin.size === file.size;
    });
  };

  const validateFile = (file: File) => {
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isValidType = config.extensions.includes(extension);
    const isValidSize = file.size / 1024 / 1024 < config.maxSize;

    if (isDuplicateFile(file)) {
      message.warning(`${file.name}: 이미 추가된 파일입니다.`);
      return false;
    }

    if (!isValidType) {
      message.error(`${file.name}: 허용되지 않은 파일 형식입니다. (허용: ${config.extensionsDisplay.join(', ')})`);
      return false;
    }

    if (!isValidSize) {
      message.error(`${file.name}: 파일 크기가 ${config.maxSize}MB를 초과합니다.`);
      return false;
    }

    return true;
  };

  const handleBeforeUpload = (file: File, selectedFiles: File[]) => {
    if (selectedFiles[0] === file) {
      const validatedList: any[] = [...fileList];
      let validCount = 0;

      selectedFiles.forEach((selectedFile, index) => {
        if (!isDuplicateFile(selectedFile, validatedList)) {
          const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
          const isValidType = config.extensions.includes(extension);
          const isValidSize = selectedFile.size / 1024 / 1024 < config.maxSize;

          const tempFile = {
            uid: `${Date.now()}-${index}`,
            name: selectedFile.name,
            status: 'done',
            originFileObj: selectedFile,
          };
          const tempList = [...validatedList, tempFile];
          const totalSize = getTotalFileSize(tempList);
          const isValidTotalSize = totalSize <= config.maxTotalSize;

          if (!isValidType) {
             message.error(`${selectedFile.name}: 허용되지 않은 파일 형식입니다. (허용: ${config.extensionsDisplay.join(', ')})`);
          } else if (!isValidSize) {
             message.error(`${selectedFile.name}: 파일 크기가 ${config.maxSize}MB를 초과합니다.`);
          } else if (!isValidTotalSize) {
             message.error(`${selectedFile.name}: 전체 파일 크기가 ${config.maxTotalSize}MB를 초과합니다. (현재: ${totalSize.toFixed(2)}MB)`);
          } else {
            validatedList.push(tempFile);
            validCount++;
          }
        } else {
           message.warning(`${selectedFile.name}: 이미 추가된 파일입니다.`);
        }
      });

      setFileList(validatedList);
      onChange?.({ fileList: validatedList });

      if (validCount > 0 && showSuccessMessage) {
        message.success(`${validCount}개의 파일이 선택되었습니다.`);
      }
    }
    return false;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(validateFile);

    if (validFiles.length > 0) {
      const newFileList = validFiles.map((file, index) => ({
        uid: `${Date.now()}-${index}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      }));

      const updatedList = multiple ? [...fileList, ...newFileList] : [newFileList[0]];
      const totalSize = getTotalFileSize(updatedList);

      if (totalSize > config.maxTotalSize) {
        message.error(`전체 파일 크기가 ${config.maxTotalSize}MB를 초과합니다. (현재: ${totalSize.toFixed(2)}MB)`);
        return;
      }

      setFileList(updatedList);
      onChange?.({ fileList: updatedList });
      if (showSuccessMessage) {
        message.success(`${validFiles.length}개의 파일이 선택되었습니다.`);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const files = items
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((file): file is File => file !== null && validateFile(file));

    if (files.length > 0) {
      const newFileList = files.map((file, index) => ({
        uid: `${Date.now()}-${index}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      }));

      const updatedList = multiple ? [...fileList, ...newFileList] : [newFileList[0]];
      const totalSize = getTotalFileSize(updatedList);

      if (totalSize > config.maxTotalSize) {
        message.error(`전체 파일 크기가 ${config.maxTotalSize}MB를 초과합니다. (현재: ${totalSize.toFixed(2)}MB)`);
        return;
      }

      setFileList(updatedList);
      onChange?.({ fileList: updatedList });
      if (showSuccessMessage) {
        message.success(`${files.length}개의 파일이 선택되었습니다.`);
      }
    }
  };

  return (
      <UploadContainer
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onPaste={handlePaste}
        tabIndex={0}
        $isDragging={isDragging}
      >
        <Upload
          fileList={[]}
          beforeUpload={handleBeforeUpload}
          accept={config.accept}
          multiple={multiple}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
  
        {fileList.length > 0 && (
          <FileListWrapper $enableScroll={enableScroll} $maxHeight={maxHeight}>
            <Upload
              fileList={fileList}
              onRemove={(file) => {
                const newFileList = fileList.filter(item => item.uid !== file.uid);
                setFileList(newFileList);
                onChange?.({ fileList: newFileList });
                message.info(`${file?.name} 파일이 제거되었습니다.`);
              }}
              showUploadList={true}
            />
          </FileListWrapper>
        )}
  
        <HelpText>
          <InfoRow>
            <RequiredMark>*</RequiredMark>
            <strong>허용 파일 형식:</strong> {config.extensionsDisplay.join(', ')}
          </InfoRow>
  
          {(mode === 'slide' || mode === 'all') && (
            <>
              <InfoRow>
                <RequiredMark>*</RequiredMark>
                <strong>개별 파일 최대 크기:</strong> {config.maxSize}MB (약 {(config.maxSize / 1024).toFixed(1)}GB)
              </InfoRow>
              <InfoRow>
                <RequiredMark>*</RequiredMark>
                <strong>전체 파일 최대 크기:</strong> {config.maxTotalSize}MB (약 {(config.maxTotalSize / 1024).toFixed(1)}GB)
              </InfoRow>
            </>
          )}
  
          <InfoRow>
            <strong>업로드 방법:</strong>
          </InfoRow>
          <MethodList>
            <li>파일 선택 버튼 클릭</li>
            <li>파일 드래그 & 드롭</li>
            <li>파일 복사 후 붙여넣기 (Ctrl+V)</li>
          </MethodList>
  
          {/* 파일이 있을 때 현재 용량 표시는 mode 상관없이(또는 slide일때만? 일단 요청사항에 따라 slide일때만 상세정보 표시지만, 현재 상태는 보여주는게 좋아보여 유지하되 slide조건을 걸 수 있음) */}
          {/* 요청사항: "개별항목 크기나 총 크기 같은건 현재로서는 슬라이드 모드일때만 보이게 해줘" -> 아래 현재 용량 status도 slide일때만 보이게 처리 */}
          {fileList.length > 0 && mode === 'slide' && (
            <InfoRow style={{ marginTop: '8px', color: '#1677ff' }}>
              {fileList.length}개의 파일 선택됨 (총 {getTotalFileSize(fileList).toFixed(2)}MB / {config.maxTotalSize}MB)
            </InfoRow>
          )}
        </HelpText>
      </UploadContainer>
    );
};

const UploadContainer = styled.div<{ $isDragging: boolean }>`
  padding: 20px;
  border: 2px dashed ${props => props.$isDragging ? '#1677ff' : '#d9d9d9'};
  border-radius: 8px;
  background-color: ${props => props.$isDragging ? '#f0f5ff' : '#fafafa'};
  transition: all 0.3s;
  outline: none;

  &:focus-within {
    border-color: #1677ff;
  }
`;

const FileListWrapper = styled.div<{ $enableScroll: boolean; $maxHeight: string }>`
  margin-top: 12px;

  ${props => props.$enableScroll && `
    max-height: ${props.$maxHeight};
    overflow-y: auto;
    overflow-x: hidden;

    /* 스크롤바 스타일링 */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  `}

  /* Upload 컴포넌트의 기본 스타일 오버라이드 */
  .ant-upload-list {
    padding: 0;
  }
`;

const HelpText = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.6;
`;

const InfoRow = styled.div`
  margin-bottom: 6px;

  strong {
    color: #595959;
    margin-right: 6px;
  }
`;

const MethodList = styled.ul`
  margin: 4px 0 0 20px;
  padding: 0;

  li {
    margin-bottom: 3px;
    color: #8c8c8c;
  }
`;

const RequiredMark = styled.span`
  color: #ff4d4f;
  margin-right: 4px;
  font-weight: bold;
`;

export default FileUploadInput;
